import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/mapConfig";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Research() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (search) params.search = search;
    api.get("/posts", { params }).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const cats = ["All", ...CATEGORIES];

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#059669] mb-3">Research & Articles</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#11181C] tracking-tight">The questions beneath the headlines</h1>
          <p className="mt-4 text-[#52606D] max-w-2xl leading-relaxed">
            Field notes, analysis and documents from the Data Centre Exposure Project. Filter by theme or search the archive.
          </p>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${category === c ? "bg-[#0B0F17] text-white border-[#0B0F17]" : "bg-white text-[#52606D] border-slate-200 hover:border-slate-400"}`}
                data-testid={`research-filter-${c.toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52606D]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the archive…"
              className="w-full pl-9 pr-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#059669]"
              data-testid="research-search-input" />
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-[#52606D]">Loading research…</div>
        ) : posts.length === 0 ? (
          <div className="py-20 text-center text-[#52606D]">No articles match your filters.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
            {posts.map((p, i) => (
              <Link key={p.id} to={`/research/${p.slug}`} className="group bg-white hover:bg-slate-100 transition-colors flex flex-col" data-testid={`research-card-${i}`}>
                {p.cover_image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img src={fileUrl(p.cover_image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[#52606D]">
                    <span className="text-[#059669]">{p.category}</span><span>·</span><span>{fmtDate(p.published_at)}</span>
                  </div>
                  <h3 className="font-serif text-xl text-[#11181C] mt-3 leading-snug group-hover:text-[#2E8BFF] transition-colors">{p.title}</h3>
                  <p className="text-sm text-[#52606D] mt-2.5 leading-relaxed flex-1">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-3 font-mono text-[11px] text-[#52606D]">
                    <span>{p.read_minutes || 5} min read</span>
                    {p.infrastructure_type && <><span>·</span><span>{p.infrastructure_type}</span></>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
